// Uses apiClient (src/core/api/client.ts) which has request interceptors that
// automatically attach the in-memory access token via getAccessToken().
// Do NOT use the plain axiosInstance here — it has no interceptors.
import apiClient from "core/api/client";
import type { CreateOrderBody } from "types/api.generated";
import type { Order, ListOrdersResponse, OrderLocationResponse } from "types/delivery.types";

/** POST /api/v1/orders — Create a new delivery order */
export const createOrder = async (data: CreateOrderBody): Promise<Order> => {
  const response = await apiClient.post<Order>("/api/v1/orders", data);
  return response.data;
};

/** GET /api/v1/orders — List the current user's orders */
export const listOrders = async (params?: {
  page?: number;
  perPage?: number;
  status?: Order["status"];
}): Promise<ListOrdersResponse> => {
  const response = await apiClient.get("/api/v1/orders", { params });
  return response.data;
};

/** GET /api/v1/orders/:id — Get a single order */
export const getOrder = async (id: string): Promise<Order> => {
  const response = await apiClient.get(`/api/v1/orders/${id}`);
  return response.data;
};

/** GET /api/v1/orders/:id/location — Vehicle, driver, GPS snapshot (poll fallback) */
export const getOrderLocation = async (
  id: string,
): Promise<OrderLocationResponse> => {
  const response = await apiClient.get<OrderLocationResponse>(
    `/api/v1/orders/${id}/location`,
  );
  return response.data;
};

/** PATCH /api/v1/orders/:id/cancel — Cancel an order */
export const cancelOrder = async (id: string): Promise<void> => {
  await apiClient.patch(`/api/v1/orders/${id}/cancel`);
};

/** POST /api/v1/payments/initialize — Initialize a Paystack card payment for an order */
export const initializeOrderPayment = async (
  orderId: string,
): Promise<{ reference: string; accessCode: string }> => {
  const response = await apiClient.post<{ reference: string; accessCode: string }>(
    "/api/v1/payments/initialize",
    { orderId },
  );
  return response.data;
};

/** POST /api/v1/payments/verify — Verify Paystack payment and confirm order after popup success */
export const verifyOrderPayment = async (
  orderId: string,
): Promise<{ status: string }> => {
  const response = await apiClient.post<{ status: string }>(
    "/api/v1/payments/verify",
    { orderId },
  );
  return response.data;
};
